import React from "react";
import type { WarmupPhase } from "../hooks/useWarmup";
import { WARMUP_MESSAGES, LOADING_COUNT } from "./WarmupMessages";

// Keyframes only — inline styles handle everything else
const KEYFRAMES = `
  @keyframes vw-pulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.72; }
  }
  @keyframes vw-shimmer {
    0%   { transform: translateX(-100%); }
    100% { transform: translateX(350%); }
  }
  @keyframes vw-d1 { 0%,20%{ opacity:0 } 30%,80%{ opacity:1 } 100%{ opacity:0 } }
  @keyframes vw-d2 { 0%,40%{ opacity:0 } 50%,90%{ opacity:1 } 100%{ opacity:0 } }
  @keyframes vw-d3 { 0%,60%{ opacity:0 } 70%,100%{ opacity:1 } }
  @keyframes vw-tip-in { from { opacity: 0; } to { opacity: 1; } }
  @keyframes vw-tip-out { from { opacity: 1; } to { opacity: 0; } }
  /* Logo size comes from the --splash-lockup-h token defined in index.html —
     one source of truth shared with the instant splash. The 2026 lockup is a
     single self-contained SVG (icon + wordmark) — do not hardcode here. */
  .vw-lockup { height: var(--splash-lockup-h); width: auto; display: block; }
  @media (max-width: 640px) {
    .vw-msg { font-size: 13px !important; }
  }
`;

function AnimatedDots() {
  const s = (anim: string): React.CSSProperties => ({
    animation: anim,
    opacity: 0,
    color: "#6b7280",
  });
  return (
    <>
      <span style={s("vw-d1 1.6s infinite")}>.</span>
      <span style={s("vw-d2 1.6s infinite")}>.</span>
      <span style={{ animation: "vw-d3 1.6s infinite", color: "#6b7280" }}>.</span>
    </>
  );
}

// Fisher-Yates shuffle, returns new array
function shuffled(arr: number[]): number[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function makeDeck(excluding = -1): number[] {
  // Skip loading-state messages (first LOADING_COUNT) — tips always show, even during load.
  const all = Array.from({ length: WARMUP_MESSAGES.length }, (_, i) => i)
    .filter(i => i >= LOADING_COUNT && i !== excluding);
  return shuffled(all);
}

const MIN_TIP_DWELL_MS = 6000;

function readPersistedTip(): { idx: number; ts: number } {
  try {
    const idx = parseInt(window.sessionStorage.getItem("vw-tip-idx") || "", 10);
    const ts = parseInt(window.sessionStorage.getItem("vw-tip-ts") || "", 10);
    // Only accept tip-section indices (>= LOADING_COUNT) — reject loading-state
    // indices written by index.html which don't map to tips in WARMUP_MESSAGES.
    if (Number.isFinite(idx) && idx >= LOADING_COUNT && idx < WARMUP_MESSAGES.length) {
      return { idx, ts: Number.isFinite(ts) ? ts : Date.now() };
    }
  } catch {}
  return { idx: 0, ts: 0 };
}

function persistTip(idx: number) {
  try {
    window.sessionStorage.setItem("vw-tip-idx", String(idx));
    window.sessionStorage.setItem("vw-tip-ts", String(Date.now()));
  } catch {}
}

// Tip cycle timings — dwell long enough to actually read, crossfade snappy so
// it never feels sluggish. Dwell + fade are independent (crossfade overlaps).
const TIP_DWELL_MS = 9000;
const TIP_FADE_MS = 700;

export default function Warmup({ phase }: { phase: WarmupPhase }) {
  const deckRef = React.useRef<number[]>([]);
  const [msgIdx, setMsgIdx] = React.useState(() => {
    // Reuse the tip already chosen by index.html or a previous mount — prevents
    // rapid-fire flips when AppLoading remounts during route/provider transitions.
    const persisted = readPersistedTip();
    if (persisted.ts && Date.now() - persisted.ts < MIN_TIP_DWELL_MS) {
      const deck = makeDeck(persisted.idx);
      deckRef.current = deck;
      return persisted.idx;
    }
    const deck = makeDeck();
    deckRef.current = deck.slice(1);
    persistTip(deck[0]);
    return deck[0];
  });
  // Previous tip held briefly during crossfade so old text fades out in place
  // while new text fades in over it — no layout drop/jump between swaps.
  const [prevIdx, setPrevIdx] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (phase !== "loading") return;
    const timer = setInterval(() => {
      setMsgIdx((prev) => {
        let next: number;
        if (deckRef.current.length === 0) {
          const newDeck = makeDeck(prev);
          deckRef.current = newDeck.slice(1);
          next = newDeck[0];
        } else {
          next = deckRef.current.shift()!;
        }
        persistTip(next);
        setPrevIdx(prev);
        // Clear the previous tip once the crossfade has fully completed so we
        // don't leave invisible nodes mounted forever.
        window.setTimeout(() => setPrevIdx(null), TIP_FADE_MS + 50);
        return next;
      });
    }, TIP_DWELL_MS);
    return () => clearInterval(timer);
  }, [phase]);

  const msg = WARMUP_MESSAGES[msgIdx];
  const prevMsg = prevIdx !== null ? WARMUP_MESSAGES[prevIdx] : null;

  // ── Root: full-screen overlay, pixel-perfect centered block ─────────────
  // The inner block has a fixed pixel height (172px = 62 logo + 24 gap + 60 msg + 24 gap + 2 bar).
  // Centering the block as a unit means NO child change can shift the bar position.
  const rootStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "var(--splash-bg)",
    zIndex: 99999,
    fontFamily: "'Inter', sans-serif",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: phase === "fading" ? 0 : 1,
    transition: "opacity 600ms ease-out",
  };

  // Fixed-height block — the entire content is locked to exactly 172px tall.
  // justifyContent: center on the root distributes space outside this block only,
  // so any text reflow inside never changes where the bar sits on screen.
  const blockStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "var(--splash-gap)",
    flexShrink: 0,
  };

  const logoWrapStyle: React.CSSProperties = {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  // Fixed-height slot sized for up to 3 lines @ 15.5px × 1.6 line-height (~75px).
  // Both old and new tips render absolutely inside this slot so a swap never
  // changes the height of surrounding content — eliminates the drop-then-jump.
  const msgWrapStyle: React.CSSProperties = {
    position: "relative",
    width: 460,
    maxWidth: "88vw",
    height: 78,
    flexShrink: 0,
    padding: "0 12px",
  };

  const msgBaseStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    color: "#111827",
    fontSize: 15.5,
    fontWeight: 400,
    letterSpacing: "0.01em",
    textAlign: "center",
    lineHeight: 1.6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 12px",
    willChange: "opacity",
  };

  // New tip fades in (0→1); previous tip fades out (1→0). Both play simultaneously
  // and run exactly TIP_FADE_MS — a true crossfade with no positional change.
  const currentMsgStyle: React.CSSProperties = {
    ...msgBaseStyle,
    opacity: 1,
    animation: prevMsg ? `vw-tip-in ${TIP_FADE_MS}ms ease-in-out both` : undefined,
  };
  const prevMsgStyle: React.CSSProperties = {
    ...msgBaseStyle,
    opacity: 0,
    pointerEvents: "none",
    animation: `vw-tip-out ${TIP_FADE_MS}ms ease-in-out both`,
  };

  const barTrackStyle: React.CSSProperties = {
    width: 140,
    height: 2,
    background: "#e5e7eb",
    borderRadius: 2,
    overflow: "hidden",
    flexShrink: 0,
  };

  const barFillStyle: React.CSSProperties = {
    width: "45%",
    height: "100%",
    background: "linear-gradient(90deg, #4C75AB 0%, #2FBF71 100%)",
    borderRadius: 2,
    animation: "vw-shimmer 1.6s ease-in-out infinite",
  };

  return (
    <>
      <style>{KEYFRAMES}</style>
      <div style={rootStyle}>
        {/* Single fixed-height block — bar can never shift regardless of text */}
        <div style={blockStyle}>
          <div style={logoWrapStyle}>
            {/* 2026 brand lockup — single SVG, sized via the shared --splash-lockup-h token. */}
            <img className="vw-lockup" src="/img/velaro-lockup-2026.svg" alt="Velaro" />
          </div>

          <div style={msgWrapStyle}>
            {prevMsg && (
              <span key={`prev-${prevIdx}`} className="vw-msg" style={prevMsgStyle}>
                <span>
                  {prevMsg.text}
                  {prevMsg.dots && <AnimatedDots />}
                </span>
              </span>
            )}
            <span key={`cur-${msgIdx}`} className="vw-msg" style={currentMsgStyle}>
              <span>
                {msg.text}
                {msg.dots && <AnimatedDots />}
              </span>
            </span>
          </div>

          <div style={barTrackStyle}>
            <div style={barFillStyle} />
          </div>
        </div>
      </div>
    </>
  );
}
